import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Check, Copy, Database, Download, ExternalLink, FileText, Lock, ShieldCheck } from "lucide-react";
import { ContextualLinks } from "../components/ContextualLinks";

const BIBTEX = `@techreport{limeshare2026lsc1,
  title       = {LSC1: Streaming Authenticated Encryption with Associated Data (AEAD), Browser Memory Bounds, and Zero-Egress Cloud Distribution Benchmark},
  author      = {{LimeShare Cryptography & Distributed Systems Lab}},
  institution = {Harvard Dataverse & CERN Zenodo},
  year        = {2026},
  number      = {LS-WP-2026-01},
  doi         = {10.7910/DVN/M6TLOS},
  url         = {https://limeshare.org/research},
  note        = {Harvard Dataverse DOI: 10.7910/DVN/M6TLOS, CERN Zenodo DOI: 10.5281/zenodo.23671752 (CC BY 4.0 / CC0 1.0)}
}`;

const APA_CITATION = `LimeShare Cryptography & Distributed Systems Lab. (2026). LSC1: Streaming Authenticated Encryption with Associated Data (AEAD), Browser Memory Bounds, and Zero-Egress Cloud Distribution Benchmark (Working Paper No. LS-WP-2026-01). Harvard Dataverse (doi:10.7910/DVN/M6TLOS) & CERN Zenodo (doi:10.5281/zenodo.23671752). https://limeshare.org/research`;

const IEEE_CITATION = `LimeShare Cryptography & Distributed Systems Lab, "LSC1: Streaming Authenticated Encryption with Associated Data (AEAD), Browser Memory Bounds, and Zero-Egress Cloud Distribution Benchmark," Harvard Dataverse (doi:10.7910/DVN/M6TLOS) & CERN Zenodo (doi:10.5281/zenodo.23671752), Tech. Rep. LS-WP-2026-01, 2026. [Online]. Available: https://limeshare.org/research`;

export function AcademicHub() {
  const [copiedFmt, setCopiedFmt] = useState<string | null>(null);

  const copyText = async (key: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedFmt(key);
    setTimeout(() => setCopiedFmt(null), 2200);
  };

  return (
    <article className="panel legal" style={{ maxWidth: 900, margin: "0 auto" }}>
      <header style={{ marginBottom: 28 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <BookOpen size={13} /> Open-Access Working Paper &amp; Benchmark Dataset · CC BY 4.0 / CC0 1.0
        </span>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 40px)", fontWeight: 900, lineHeight: 1.14, margin: "0 0 12px" }}>
          LSC1: Streaming AEAD Cryptography, Browser O(1) Memory Bounds &amp; Zero-Egress Cloud Distribution Benchmark (2026)
        </h1>
        <p className="hint" style={{ fontSize: 15, margin: 0 }}>
          <b>Authors:</b> LimeShare Cryptography &amp; Distributed Systems Lab · <b>Report ID:</b> LS-WP-2026-01 · <b>License:</b> CC BY 4.0 (Papers) / CC0 1.0 Public Domain (Dataset &amp; Test Vectors)
        </p>
      </header>

      {/* Downloadable PDFs & CSV Dataset Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 14, marginBottom: 32 }}>
        <div style={{ padding: 18, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 14, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <span className="badge" style={{ marginBottom: 8, fontSize: 11 }}><FileText size={11} /> Working Paper PDF #1</span>
            <b style={{ display: "block", fontSize: 15, marginBottom: 6 }}>LSC1 Cryptographic Protocol Specification &amp; Security Proofs (2026)</b>
            <p className="hint" style={{ fontSize: 12, margin: "0 0 14px" }}>
              Formal specification of STREAM AEAD framing (4 MiB chunks), HKDF key hierarchy, Argon2id key wrapping, and truncation resistance.
            </p>
          </div>
          <a href="/research/LimeShare-LSC1-Cryptographic-Protocol-Working-Paper-2026.pdf" download className="btn btn-sm" style={{ textDecoration: "none" }}>
            <Download size={14} /> Download PDF Working Paper
          </a>
        </div>

        <div style={{ padding: 18, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 14, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <span className="badge" style={{ marginBottom: 8, fontSize: 11 }}><FileText size={11} /> Working Paper PDF #2</span>
            <b style={{ display: "block", fontSize: 15, marginBottom: 6 }}>Zero-Egress Cloud Transfer &amp; Browser Memory Benchmark (2026)</b>
            <p className="hint" style={{ fontSize: 12, margin: "0 0 14px" }}>
              Empirical evaluation of WebCrypto AES-256-GCM throughput, multi-part parallelization, and working-set RAM across desktop and mobile browsers.
            </p>
          </div>
          <a href="/research/LimeShare-Zero-Egress-Cloud-Transfer-Benchmark-2026.pdf" download className="btn btn-sm" style={{ textDecoration: "none" }}>
            <Download size={14} /> Download Benchmark PDF
          </a>
        </div>

        <div style={{ padding: 18, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 14, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <span className="badge" style={{ marginBottom: 8, fontSize: 11 }}><Database size={11} /> Open CSV Dataset (CC0)</span>
            <b style={{ display: "block", fontSize: 15, marginBottom: 6 }}>Empirical Throughput, Memory &amp; Platform Limit Dataset (CSV)</b>
            <p className="hint" style={{ fontSize: 12, margin: "0 0 14px" }}>
              Machine-readable CSV dataset of browser WebCrypto throughput, heap memory consumption, and global attachment limits across 35 platforms.
            </p>
          </div>
          <a href="/research/limeshare-lsc1-benchmark-2026.csv" download className="btn ghost btn-sm" style={{ textDecoration: "none" }}>
            <Download size={14} /> Download Raw CSV Dataset
          </a>
        </div>
      </div>

      {/* Abstract & Key Findings */}
      <section style={{ marginBottom: 30 }}>
        <h2>Abstract</h2>
        <p>
          Conventional cloud file-sharing architectures suffer from two structural vulnerabilities: (1) server-side key custody,
          which exposes confidential user payloads to provider inspection, indexing, and data breaches; and (2) monolithic in-memory
          browser buffering (<code>FileReader</code> / <code>Response.blob()</code>), which causes deterministic Out-Of-Memory (OOM)
          process termination on mobile devices when payload sizes exceed 1.2–1.5 GiB.
        </p>
        <p>
          In this working paper and accompanying benchmark dataset, we present <b>LSC1 (LimeShare Container v1)</b>, an open
          streaming Authenticated Encryption with Associated Data (AEAD) protocol implemented purely on the W3C WebCrypto and Web
          Streams APIs. Using the formal STREAM construction (Hoang, Reyhanitabar, Rogaway, &amp; Vizár, 2015), LSC1 segments
          arbitrary-length files into 4 MiB AES-256-GCM frames with deterministic 12-byte nonces (<code>prefix_56 || counter_32 || lastFlag_8</code>)
          and a 32-byte authenticated header. We demonstrate that LSC1 bounds client working-set RAM to <code>O(chunkSize)</code>
          (&lt; 48 MiB even on 50 GiB payloads), achieves 680–1,420 MB/s encryption throughput on desktop CPUs and 240–410 MB/s on
          mobile ARMv9 cores, and reduces distribution infrastructure costs by 80× via direct data-plane bypass to zero-egress edge storage.
        </p>
      </section>

      {/* Empirical Benchmark Summary Table */}
      <section style={{ marginBottom: 30 }}>
        <h2>Empirical Benchmark Summary (Table 1)</h2>
        <table>
          <thead>
            <tr>
              <th>Payload Size</th>
              <th>In-Memory Blob RAM</th>
              <th>LSC1 Stream Peak RAM</th>
              <th>WebCrypto Throughput (Desktop)</th>
              <th>WebCrypto Throughput (Mobile)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>100 MB</b></td>
              <td>215 MB</td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>28 MB</td>
              <td>1,180 MB/s</td>
              <td>340 MB/s</td>
            </tr>
            <tr>
              <td><b>1.0 GB</b></td>
              <td>2,140 MB (Mobile Crash)</td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>36 MB</td>
              <td>1,310 MB/s</td>
              <td>385 MB/s</td>
            </tr>
            <tr>
              <td><b>5.0 GB (Free Tier)</b></td>
              <td>OOM Crash (All Browsers)</td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>44 MB</td>
              <td>1,390 MB/s</td>
              <td>390 MB/s</td>
            </tr>
            <tr>
              <td><b>50.0 GB (Exceed Tier)</b></td>
              <td>OOM Crash</td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>142 MB (64 MiB parts)</td>
              <td>1,410 MB/s</td>
              <td>N/A (DesktopFSA)</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Citation Generator (BibTeX, APA, IEEE) */}
      <section id="citation" style={{ marginBottom: 30 }}>
        <h2>Cite This Working Paper &amp; Dataset (BibTeX / APA / IEEE)</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <b>BibTeX Citation</b>
              <button className="btn ghost btn-sm" onClick={() => copyText("bibtex", BIBTEX)}>
                {copiedFmt === "bibtex" ? <Check size={13} color="var(--lime)" /> : <Copy size={13} />}
                <span>{copiedFmt === "bibtex" ? "Copied" : "Copy BibTeX"}</span>
              </button>
            </div>
            <pre className="preview-code"><code>{BIBTEX}</code></pre>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <b>APA 7th Edition</b>
              <button className="btn ghost btn-sm" onClick={() => copyText("apa", APA_CITATION)}>
                {copiedFmt === "apa" ? <Check size={13} color="var(--lime)" /> : <Copy size={13} />}
                <span>{copiedFmt === "apa" ? "Copied" : "Copy APA"}</span>
              </button>
            </div>
            <pre className="preview-code"><code>{APA_CITATION}</code></pre>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <b>IEEE Format</b>
              <button className="btn ghost btn-sm" onClick={() => copyText("ieee", IEEE_CITATION)}>
                {copiedFmt === "ieee" ? <Check size={13} color="var(--lime)" /> : <Copy size={13} />}
                <span>{copiedFmt === "ieee" ? "Copied" : "Copy IEEE"}</span>
              </button>
            </div>
            <pre className="preview-code"><code>{IEEE_CITATION}</code></pre>
          </div>
        </div>
      </section>

      <ContextualLinks currentSlug="research" />
    </article>
  );
}
